// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/RelatoriosFinanceiro.tsx
//  Relatórios Financeiros: Contas a Receber, Contas a Pagar, Compras.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { ArrowDownCircle, ArrowUpCircle, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useRelContasReceberAnalitico,
  useRelContasPagarAnalitico,
  useRelComprasFornecedor,
} from '@/features/relatorios/hooks/useRelatorios';
import { BotaoExportar } from '@/components/BotaoExportar';

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const today    = () => new Date().toISOString().slice(0, 10);
const daysAgo  = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const daysNext = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const anoInicio = () => `${new Date().getFullYear()}-01-01`;

const PAGE_SIZES = [10, 20, 50] as const;

const STATUS_COLORS: Record<string, string> = {
  'Pago':      'bg-green-500/10 text-green-600',
  'Vencido':   'bg-red-500/10 text-red-600',
  'A Vencer':  'bg-yellow-500/10 text-yellow-600',
  'Cancelado': 'bg-gray-500/10 text-gray-500',
};

function FiltrosPeriodo({ label = 'Vencimento', dtInicio, dtFim, setDtInicio, setDtFim }: {
  label?: string; dtInicio: string; dtFim: string;
  setDtInicio: (v: string) => void; setDtFim: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">{label} de</span>
        <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="w-40" />
      </div>
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">{label} até</span>
        <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="w-40" />
      </div>
    </div>
  );
}

function PaginacaoBar({ pagina, setPagina, tam, setTam, total, totalPaginas }: {
  pagina: number; setPagina: (p: number) => void;
  tam: number; setTam: (t: number) => void;
  total: number; totalPaginas: number;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t text-sm text-muted-foreground">
      <span>Total: {total} registros</span>
      <div className="flex items-center gap-3">
        <Select value={String(tam)} onValueChange={(v) => { setTam(Number(v)); setPagina(1); }}>
          <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>{PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}><ChevronLeft className="h-4 w-4" /></Button>
        <span>Pág {pagina}/{totalPaginas}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

// ── Contas a Receber ──────────────────────────────────────────────────────────

function TabContasReceber() {
  const [dtVencInicio, setDtVencInicio] = useState(daysAgo(30));
  const [dtVencFim,    setDtVencFim]    = useState(daysNext(30));
  const [isPago,       setIsPago]       = useState<boolean | undefined>(undefined);
  const [pagina,       setPagina]       = useState(1);
  const [tam,          setTam]          = useState(20);

  const { data, isLoading } = useRelContasReceberAnalitico({
    dtVencInicio, dtVencFim, isPago, pagina, tamanhoPagina: tam,
  });
  const exportParams = useMemo(() => ({ dtVencInicio, dtVencFim, isPago }), [dtVencInicio, dtVencFim, isPago]);

  const totalSaldo = data?.itens.reduce((s, x) => s + x.valorSaldo, 0) ?? 0;
  const totalPago  = data?.itens.reduce((s, x) => s + x.valorPago,  0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <FiltrosPeriodo dtInicio={dtVencInicio} dtFim={dtVencFim} setDtInicio={(v) => { setDtVencInicio(v); setPagina(1); }} setDtFim={(v) => { setDtVencFim(v); setPagina(1); }} />
        <Select value={isPago === undefined ? 'todos' : isPago ? 'pago' : 'aberto'}
          onValueChange={(v) => { setIsPago(v === 'todos' ? undefined : v === 'pago'); setPagina(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Situação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pago">Pagos</SelectItem>
            <SelectItem value="aberto">Em Aberto</SelectItem>
          </SelectContent>
        </Select>
        <BotaoExportar endpoint="/api/relatorios/financeiro/contas-receber/exportar" params={exportParams} />
      </div>
      {data && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Saldo em Aberto</p><p className="text-lg font-bold tabular-nums text-red-600">{fmtCur(totalSaldo)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Total Recebido</p><p className="text-lg font-bold tabular-nums text-green-600">{fmtCur(totalPago)}</p></div>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Documento</TableHead>
              <TableHead>Emissão</TableHead>
              <TableHead>Vencimento</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Valor Original</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium max-w-[140px] truncate" title={r.nomeCliente}>{r.nomeCliente}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.numDocumento ?? '—'}</TableCell>
                <TableCell className="text-sm">{new Date(r.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell className="text-sm">{new Date(r.dtVencimento).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.dtPagamento ? new Date(r.dtPagamento).toLocaleDateString('pt-BR') : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorOriginal)}</TableCell>
                <TableCell className={`text-right tabular-nums font-semibold ${r.valorSaldo > 0 ? 'text-red-600' : ''}`}>{fmtCur(r.valorSaldo)}</TableCell>
                <TableCell><Badge className={STATUS_COLORS[r.status] ?? ''}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tam={tam} setTam={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
    </div>
  );
}

// ── Contas a Pagar ────────────────────────────────────────────────────────────

function TabContasPagar() {
  const [dtVencInicio, setDtVencInicio] = useState(daysAgo(30));
  const [dtVencFim,    setDtVencFim]    = useState(daysNext(30));
  const [isPago,       setIsPago]       = useState<boolean | undefined>(undefined);
  const [pagina,       setPagina]       = useState(1);
  const [tam,          setTam]          = useState(20);

  const { data, isLoading } = useRelContasPagarAnalitico({
    dtVencInicio, dtVencFim, isPago, pagina, tamanhoPagina: tam,
  });
  const exportParams = useMemo(() => ({ dtVencInicio, dtVencFim, isPago }), [dtVencInicio, dtVencFim, isPago]);

  const totalSaldo = data?.itens.reduce((s, x) => s + x.valorSaldo, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <FiltrosPeriodo dtInicio={dtVencInicio} dtFim={dtVencFim} setDtInicio={(v) => { setDtVencInicio(v); setPagina(1); }} setDtFim={(v) => { setDtVencFim(v); setPagina(1); }} />
        <Select value={isPago === undefined ? 'todos' : isPago ? 'pago' : 'aberto'}
          onValueChange={(v) => { setIsPago(v === 'todos' ? undefined : v === 'pago'); setPagina(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Situação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pago">Pagos</SelectItem>
            <SelectItem value="aberto">Em Aberto</SelectItem>
          </SelectContent>
        </Select>
        <BotaoExportar endpoint="/api/relatorios/financeiro/contas-pagar/exportar" params={exportParams} />
      </div>
      {data && (
        <div className="rounded-lg border p-3 text-sm w-fit">
          <p className="text-xs text-muted-foreground">Saldo em Aberto (página)</p>
          <p className="text-lg font-bold tabular-nums text-red-600">{fmtCur(totalSaldo)}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fornecedor</TableHead>
              <TableHead>Documento</TableHead>
              <TableHead>Emissão</TableHead>
              <TableHead>Vencimento</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium max-w-[140px] truncate" title={r.nomeFornecedor}>{r.nomeFornecedor}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.numDocumento ?? '—'}</TableCell>
                <TableCell className="text-sm">{new Date(r.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell className="text-sm">{new Date(r.dtVencimento).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.dtPagamento ? new Date(r.dtPagamento).toLocaleDateString('pt-BR') : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorTotal)}</TableCell>
                <TableCell className={`text-right tabular-nums font-semibold ${r.valorSaldo > 0 ? 'text-red-600' : ''}`}>{fmtCur(r.valorSaldo)}</TableCell>
                <TableCell><Badge className={STATUS_COLORS[r.status] ?? ''}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tam={tam} setTam={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
    </div>
  );
}

// ── Compras ───────────────────────────────────────────────────────────────────

function TabCompras() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());

  const { data, isLoading } = useRelComprasFornecedor({ dtInicio, dtFim });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);
  const totalComprado = data?.reduce((s, x) => s + x.totalComprado, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo label="Emissão" dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/financeiro/compras/exportar" params={exportParams} />
      </div>
      {data && (
        <div className="rounded-lg border p-3 text-sm w-fit">
          <p className="text-xs text-muted-foreground">Total Comprado no Período</p>
          <p className="text-lg font-bold tabular-nums text-blue-600">{fmtCur(totalComprado)}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fornecedor</TableHead>
              <TableHead className="text-right">Qtd Títulos</TableHead>
              <TableHead className="text-right">Total Comprado</TableHead>
              <TableHead className="text-right">Saldo em Aberto</TableHead>
              <TableHead>Última Compra</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.length ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.fornecedorId}>
                <TableCell className="font-medium">{r.nomeFornecedor}</TableCell>
                <TableCell className="text-right">{r.qtdTitulos}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalComprado)}</TableCell>
                <TableCell className={`text-right tabular-nums ${r.totalSaldo > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{fmtCur(r.totalSaldo)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(r.ultimaCompra).toLocaleDateString('pt-BR')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

const RelatoriosFinanceiro = () => (
  <div className="p-6 space-y-6">
    <div>
      <h1 className="text-3xl font-bold text-foreground">Relatórios Financeiros</h1>
      <p className="text-muted-foreground mt-1">Contas a receber, contas a pagar e compras por fornecedor.</p>
    </div>
    <Tabs defaultValue="receber">
      <TabsList>
        <TabsTrigger value="receber" className="gap-2"><ArrowDownCircle className="h-4 w-4" />Contas a Receber</TabsTrigger>
        <TabsTrigger value="pagar"   className="gap-2"><ArrowUpCircle   className="h-4 w-4" />Contas a Pagar</TabsTrigger>
        <TabsTrigger value="compras" className="gap-2"><ShoppingCart    className="h-4 w-4" />Compras</TabsTrigger>
      </TabsList>
      <TabsContent value="receber"> <Card><CardHeader><CardTitle>Contas a Receber — Analítico</CardTitle></CardHeader><CardContent><TabContasReceber /></CardContent></Card></TabsContent>
      <TabsContent value="pagar">   <Card><CardHeader><CardTitle>Contas a Pagar — Analítico</CardTitle></CardHeader><CardContent><TabContasPagar /></CardContent></Card></TabsContent>
      <TabsContent value="compras"> <Card><CardHeader><CardTitle>Compras por Fornecedor</CardTitle></CardHeader><CardContent><TabCompras /></CardContent></Card></TabsContent>
    </Tabs>
  </div>
);

export default RelatoriosFinanceiro;
